import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, Search, Globe, ExternalLink, Loader2, FileText } from 'lucide-react';
import { API } from '@/App';

const PagesManagementPage = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newSlug, setNewSlug] = useState('');

  useEffect(() => { fetchPages(); }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/pages`);
      setPages(res.data);
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Supprimer cette page ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/pages/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Page supprimée');
      fetchPages();
    } catch { toast.error('Erreur'); }
  };

  const handleCreate = () => {
    if (!newTitle.trim()) return;
    const slug = newSlug.trim() || newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    navigate(`/admin/page-builder/${slug}?title=${encodeURIComponent(newTitle)}`);
    setCreateOpen(false);
  };

  const filtered = pages.filter(p =>
    !searchQuery ||
    (p.page_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.page_slug || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pages managed elsewhere (informational only)
  const managedPages = [
    { title: "Page d'accueil", path: '/admin/homepage-cms', desc: "Section CMS dediee" },
    { title: 'Qui sommes-nous / FAQ / Partenaires / Bureau', path: '/admin/institutional-cms', desc: "Pages institutionnelles" },
    { title: 'Blog / Actualites', path: '/admin/blog', desc: "Gestion des articles" },
    { title: 'Événements', path: '/admin/events', desc: "Gestion des événements" },
    { title: 'Formations', path: '/admin/formations', desc: "Gestion des formations" },
    { title: 'Contenu Patients', path: '/admin/patient-content', desc: "CMS patient" },
  ];

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl mx-auto" data-testid="pages-management">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"><Globe size={20} className="text-white" /></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: "'Barlow', sans-serif" }}>Pages personnalisees</h1>
              <p className="text-xs text-gray-500">Créez des pages libres avec le Page Builder</p>
            </div>
          </div>
          <Button onClick={() => { setNewTitle(''); setNewSlug(''); setCreateOpen(true); }} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="create-page-btn">
            <Plus size={16} className="mr-2" /> Nouvelle page
          </Button>
        </div>

        {/* Info: managed pages */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2">Pages gerees par sections dediees</p>
          <div className="flex flex-wrap gap-2">
            {managedPages.map(mp => (
              <button key={mp.path} onClick={() => navigate(mp.path)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-lg text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-100 transition-all" data-testid={`managed-${mp.path}`}>
                <ExternalLink size={11} /> {mp.title}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        {pages.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Rechercher une page..." className="pl-10" />
          </div>
        )}

        {/* Dynamic Pages */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-gray-400" size={24} /></div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <FileText size={40} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2">Aucune page personnalisee</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Utilisez le Page Builder pour créer des pages sur mesure (mentions legales, CGU, landing pages, etc.)
            </p>
            <Button onClick={() => { setNewTitle(''); setNewSlug(''); setCreateOpen(true); }} className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white">
              <Plus size={16} className="mr-2" /> Créer une page
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(page => (
              <div key={page.id || page.page_slug} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow group" data-testid={`page-${page.page_slug}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sm text-gray-900 truncate">{page.page_title || 'Sans titre'}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">/{page.page_slug}</p>
                  </div>
                  <Badge className={`ml-2 text-[10px] ${page.published ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                    {page.published ? 'Publié' : 'Brouillon'}
                  </Badge>
                </div>
                {page.meta_description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{page.meta_description}</p>}
                <p className="text-xs text-gray-400 mb-3">{page.content_blocks?.length || 0} bloc(s) de contenu</p>
                <div className="flex items-center gap-2 pt-2 border-t border-gray-50">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/admin/page-builder/${page.page_slug}`)} className="flex-1 text-xs">
                    <Edit size={13} className="mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(`/pages/${page.page_slug}`, '_blank')} disabled={!page.published} className="text-xs px-2">
                    <Eye size={13} />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(page.page_slug)} className="text-xs px-2 text-red-500 hover:text-red-700">
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Nouvelle page</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Titre de la page *</Label>
                <Input value={newTitle} onChange={e => { setNewTitle(e.target.value); setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')); }}
                  placeholder="Mentions legales" data-testid="new-page-title" />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-sm text-gray-500">/pages/</span>
                  <Input value={newSlug} onChange={e => setNewSlug(e.target.value)} placeholder="mentions-legales" className="flex-1" data-testid="new-page-slug" />
                </div>
              </div>
              <Button onClick={handleCreate} disabled={!newTitle.trim()} className="w-full bg-[#1e3a5f] hover:bg-[#162d4a] text-white" data-testid="confirm-create-page">
                Ouvrir le Page Builder
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default PagesManagementPage;
